import * as React from 'react'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { elementSubtotal } from '../form/element'
import Photo from '../form/photo'
import ElementInput from '../form/element_input'
import Uploader from '../../../uploader_v2_app/uploader'
import InformationField from './information_field'
import { OrderElement, Template } from '../order_types'

type TemplatePreviewContentProps = {
  currentTemplateData?: Template
  addElement: (element: OrderElement | Template, key: string) => void
  currentElement: OrderElement
  currentReplaceElementId: string
  allowImageUpload: boolean
  uploaderOptions: any
  initUploader?: (id: string, options: any) => void
}

export default function TemplatePreviewContent(props: TemplatePreviewContentProps) {
  const {
    currentTemplateData,
    addElement,
    currentElement,
    currentReplaceElementId,
    allowImageUpload,
    uploaderOptions,
    initUploader,
  } = props

  const [uploaderInitialized, setUploaderInitialized] = useState(false)
  const uploaderState = useSelector((state: any) => state.uploader)

  const { I18n } = window as any
  const selectedElement: Template | OrderElement = currentElement || {
    id: null,
    key: uuidv4(),
    scope_id: null,
    name: '',
    template: currentTemplateData,
    replace_element_id: currentReplaceElementId || null,
    uploader_options: uploaderOptions,
    stock_level: null,
  }
  const [element, setElement] = useState(selectedElement)

  useEffect(() => {
    if (selectedElement) {
      const elementUploaderOptions = selectedElement.uploader_options
      initUploader(elementUploaderOptions.buttonId, elementUploaderOptions)
      setUploaderInitialized(true)
    }
  }, [])

  if (!element || (currentElement && element && element.key !== currentElement.key)) {
    setElement(selectedElement)
  }

  const fields = element.template?.fields || {}
  const stockLevel = element.stock_level || element.template?.stock_level
  const description = currentTemplateData?.fields?.description?.default_value
  const replaceText = currentReplaceElementId ? ` ${I18n.t('elements.order_form.replace_suffix')}` : ''
  const defaultValue = element.template?.fields?.name?.default_value || ''
  const elementName = defaultValue + replaceText

  const infoFields = ['sku', 'sub_group', 'department', 'price'].map((field) => {
    const fieldDefinition = fields[field]
    let value = null
    if (fieldDefinition !== undefined) {
      const relationValue = fieldDefinition.relation_value
      value = relationValue === undefined ? fieldDefinition.default_value : relationValue
    }
    if (field === 'price') {
      value = parseFloat(value).toFixed(2)
    }
    const caption = I18n.t(`activerecord.attributes.elements/base_element.${field}`)
    return <InformationField key={`info-field-${field}`} caption={caption} value={value} />
  })
  const handleSubmit = (e) => {
    e.preventDefault()
    const uploader = uploaderState?.[element.uploader_options.buttonId]
    const files = uploader?.files || []
    const newElement = {
      ...element,
      uploader_options: { ...element.uploader_options, files },
    }
    addElement(newElement, newElement.key)
  }

  return (
    <div className="template-preview mt-3">
      <form onSubmit={handleSubmit}>
        <h3 className="preview-header">{elementName}</h3>
        <div className="template-preview-description">{description}</div>
        <div className="row mb-4">
          <div className="col-sm-4 col-lg-3">
            <Photo element={element} />
            <div className="subtotal mt-2">
              {I18n.t('activerecord.attributes.elements/base_element.subtotal')}:
              <span className="font-weight-bold">{elementSubtotal(element).toFixed(2)}</span>
            </div>
          </div>
          <div className="col-sm-8 col-lg-9">
            <div className="row">
              {infoFields}
              {(stockLevel || stockLevel === 0) && (
                <InformationField
                  key="info-field-stock"
                  caption={I18n.t('elements.order_form.stock_level')}
                  value={stockLevel}
                />
              )}
            </div>

            <div className="row mt-3">
              <ElementInput field="amount" element={element} name="amount" updateElement={setElement} />
              <ElementInput field="width" element={element} name="width" updateElement={setElement} />
              <ElementInput field="height" element={element} name="height" updateElement={setElement} />
              <ElementInput field="note" element={element} name="note" updateElement={setElement} />
            </div>

            {element && allowImageUpload && uploaderInitialized && (
              <div className="row mt-3">
                <div className="col-12">
                  <Uploader buttonId={element.uploader_options.buttonId} />
                </div>
              </div>
            )}

            <div>
              <button type="submit" className="add-element btn btn-success mt-3">
                {currentElement ? I18n.t('elements.actions.update_element') : I18n.t('elements.actions.add_element')}
              </button>
            </div>
            <input type="hidden" name="template_id" value={element.template?.id} />
          </div>
          <hr className="col mt-4" />
        </div>
      </form>
    </div>
  )
}